import { useScrollAnimation } from '../hooks/useScrollAnimation';

const services = [
  {
    number: '01',
    title: 'Campaign Management',
    description: 'Full-service PPC management across Sponsored Products, Sponsored Brands, Sponsored Display, and DSP with AI-powered bid optimization.',
  },
  {
    number: '02',
    title: 'Account Strategy',
    description: 'Comprehensive account planning, growth roadmaps, and strategic recommendations tailored to your business goals.',
  },
  {
    number: '03',
    title: 'AI Intelligence Layer',
    description: 'Proprietary AI that connects data sources, surfaces opportunities, and automates routine decisions across your account.',
  },
  {
    number: '04',
    title: 'Performance Analytics',
    description: 'Real-time dashboards tracking ACOS, TACOS, conversion rates, and growth opportunities with actionable insights.',
  },
  {
    number: '05',
    title: 'Buy Box Monitoring',
    description: '24/7 monitoring of Buy Box status, competitor pricing, MAP violations, and instant alerts when you\'re at risk.',
  },
  {
    number: '06',
    title: 'Keyword Optimization',
    description: 'Automated keyword harvesting, negative keyword mining, bid adjustments, and search term analysis.',
  },
  {
    number: '07',
    title: 'Budget Management',
    description: 'Real-time budget tracking, smart pacing, dayparting controls, and proactive alerts to prevent overspend.',
  },
  {
    number: '08',
    title: 'Reporting & Analytics',
    description: 'Weekly, monthly, and custom reports covering sales, orders, payments, fulfillment, brand analytics, and fee reconciliation.',
  },
  {
    number: '09',
    title: 'Catalog & Listing Management',
    description: 'Product listing creation and optimization, bulk uploads, A+ Content, image and video management, and item classification.',
  },
  {
    number: '10',
    title: 'Inventory Management',
    description: 'FBA inventory management, shipment creation, replenishment planning, global FBA, and Amazon Warehousing & Distribution (AWD).',
  },
  {
    number: '11',
    title: 'Account Settings Management',
    description: 'Configuration and management of shipping settings, return policies, tax settings, fulfillment preferences, and messaging access.',
  },
  {
    number: '12',
    title: 'Brand Management',
    description: 'Brand Registry support, Amazon Stores design, Posts management, customer engagement campaigns, A/B testing, and Brand Analytics.',
  },
  {
    number: '13',
    title: 'Advertising & Promotions',
    description: 'Deals, Lightning Deals, coupons, Prime Exclusive Discounts, Vine enrollment, and promotional campaign management.',
  },
  {
    number: '14',
    title: 'Orders & Returns Management',
    description: 'Order monitoring, return authorization and processing, SAFE-T claim management, and buyer offer management.',
  },
  {
    number: '15',
    title: 'Financial Reporting & Reconciliation',
    description: 'Payments reporting, seller fee invoice review, sales tax report analysis, and fulfillment cost reconciliation.',
  },
  {
    number: '16',
    title: 'Account Health & Compliance',
    description: 'Voice of the Customer monitoring, appeal management, compliance central oversight, and proactive performance issue resolution.',
  },
];

export default function CompactServicesSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative bg-[#05060B] grid-bg py-20 lg:py-24 overflow-hidden"
    >
      <div className="w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
              Our Services
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
              Full-service management,{' '}
              <span className="text-cyan-400 cyan-glow">AI-enhanced.</span>
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              Everything you need to grow on Amazon, in one partnership.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-cyan-500/10 rounded-2xl overflow-hidden">
            {services.map((service, index) => (
              <div
                key={index}
                className={`bg-[#05060B] p-6 lg:p-8 group hover:bg-dark-900/50 transition-all duration-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 50 + 200}ms` }}
              >
                <div className="flex items-start gap-4">
                  <span className="text-cyan-500/30 font-heading font-bold text-2xl group-hover:text-cyan-400 transition-colors">
                    {service.number}
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-dark-400 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
